import { forwardRef, React, useEffect, useRef, useState, useImperativeHandle } from 'react';
import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

function valueToHTML(text, colors) {
  if (!text) return '';
  return text.split(/(\{[^}]+\})/).map(part => {
    if (/^\{[^}]+\}$/.test(part)) {
      const display = part.slice(1, -1);
      const safeVal = part.replace(/"/g, '&quot;');
      // 칩 뒤에 커서가 위치할 수 있도록 ZWSP(폭 없는 공백) 추가 //
      return (
        `<span data-value="${safeVal}" contenteditable="false" style=` +
        `"display:inline-flex;align-items:center;padding:0 8px;height:22px;` +
        `border-radius:5px;background-color:${colors.primaryAlpha20};` +
        `font-family:NotoSansKR-Regular;font-size:14px;color:${colors.text};` +
        `vertical-align:middle;cursor:default;user-select:none;margin:0 2px;"` +
        `>${display}</span>\u200B`
      );
    }
    return part
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  }).join('');
}

function domToValue(container) {
  let result = '';
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent;
    }
    else if (node.nodeName === 'SPAN' && node.dataset.value) {
      result += node.dataset.value;
    }
    else if (node.nodeName === 'BR') {
      result += '\n';
    }
    else if ((node.nodeName === 'DIV' || node.nodeName === 'P') && node !== container) {
      Array.from(node.childNodes).forEach(walk);
      if (node.nextSibling) result += '\n';
    }
    else {
      Array.from(node.childNodes).forEach(walk);
    }
  };
  Array.from(container.childNodes).forEach(walk);
  // 커서 위치용 ZWSP는 실제 값에서 제거 //
  return result.replace(/\u200B/g, '');
}

const EditSettingMessageInput = forwardRef((props, ref) => {
  const { value, setValue, placeholder } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [byte, setByte] = useState(0);
  const divRef = useRef(null);
  const lastValueRef = useRef(null);
  const savedRangeRef = useRef(null);

  // 마운트 시 초기화
  useEffect(() => {
    if (divRef.current && lastValueRef.current === null) {
      lastValueRef.current = value;
      divRef.current.innerHTML = valueToHTML(value, theme);
      setByte(checkValueByte(value));
    }
  }, []);

  // 외부(부모)에서 value가 바뀔 때 DOM 동기화
  useEffect(() => {
    if (lastValueRef.current === null) return;
    if (value !== lastValueRef.current && divRef.current) {
      lastValueRef.current = value;
      divRef.current.innerHTML = valueToHTML(value, theme);
      setByte(checkValueByte(value));
    }
  }, [value]);

  useImperativeHandle(ref, () => ({
    insertVariable: (varValue) => {
      const div = divRef.current;
      if (!div) return;
      // 포커스 복귀 시 스크롤이 맨 위로 튀지 않도록 preventScroll 사용 //
      div.focus({ preventScroll: true });

      const sel = window.getSelection();
      const chip = buildChip(varValue);

      // 저장해 둔 커서 위치가 유효하면 그 자리에, 없으면 맨 끝에 삽입 //
      const savedRange = savedRangeRef.current;
      const hasSavedCaret = savedRange && div.contains(savedRange.commonAncestorContainer);

      if (hasSavedCaret) {
        sel.removeAllRanges();
        sel.addRange(savedRange);

        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(chip);
      }
      else {
        div.appendChild(chip);
      }

      // 칩 뒤에 커서가 위치할 수 있도록 ZWSP(폭 없는 공백) 삽입 //
      const trailingSpace = document.createTextNode('\u200B');
      chip.parentNode.insertBefore(trailingSpace, chip.nextSibling);

      const newRange = document.createRange();
      newRange.setStartAfter(trailingSpace);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
      savedRangeRef.current = newRange.cloneRange();

      const newValue = domToValue(div);
      lastValueRef.current = newValue;
      setByte(checkValueByte(newValue));
      setValue(newValue);
    }
  }));

  // 에디터 내 현재 커서 위치 저장 (변수 버튼 클릭으로 포커스를 잃기 전 위치 보존) //
  function saveSelection() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (divRef.current && divRef.current.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  }

  function buildChip(varValue) {
    const span = document.createElement('span');
    span.dataset.value = varValue;
    span.setAttribute('contenteditable', 'false');
    span.textContent = varValue.slice(1, -1);
    Object.assign(span.style, {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0 8px',
      height: '22px',
      borderRadius: '5px',
      backgroundColor: theme.primaryAlpha20,
      fontFamily: 'NotoSansKR-Regular',
      fontSize: '14px',
      color: theme.text,
      verticalAlign: 'middle',
      cursor: 'default',
      userSelect: 'none',
      margin: '0 2px',
    });
    return span;
  }

  function handleInput() {
    const div = divRef.current;
    if (!div) return;

    if (!div.textContent.trim() && !div.querySelector('[data-value]')) {
      div.innerHTML = '';
    }

    const newValue = domToValue(div);
    const newByte = checkValueByte(newValue);
    if (newByte > 2000) return;

    lastValueRef.current = newValue;
    setByte(newByte);
    setValue(newValue);
  }

  function handleKeyDown(event) {
    if (event.key === '{' || event.key === '}') {
      event.preventDefault();
      return;
    }
    if (event.key === 'Backspace') {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return;

      const { startContainer, startOffset } = range;

      // 칩 뒤 ZWSP 안에 커서가 있을 때는 ZWSP와 칩을 함께 삭제 //
      if (startContainer.nodeType === Node.TEXT_NODE && startContainer.textContent === '\u200B') {
        const chipBeforeSpace = startContainer.previousSibling;

        if (chipBeforeSpace && chipBeforeSpace.nodeName === 'SPAN' && chipBeforeSpace.dataset.value) {
          event.preventDefault();
          startContainer.remove();
          chipBeforeSpace.remove();
          const newValue = domToValue(divRef.current);
          lastValueRef.current = newValue;
          setByte(checkValueByte(newValue));
          setValue(newValue);
          return;
        }
      }

      let prev = startOffset === 0 ? startContainer.previousSibling : null;
      if (!prev && startOffset === 0 && startContainer.parentNode !== divRef.current) {
        prev = startContainer.parentNode.previousSibling;
      }

      if (prev && prev.nodeName === 'SPAN' && prev.dataset.value) {
        event.preventDefault();
        prev.remove();
        const newValue = domToValue(divRef.current);
        lastValueRef.current = newValue;
        setByte(checkValueByte(newValue));
        setValue(newValue);
      }
    }
  }

  function checkValueByte(str) {
    let byteLength = 0;
    for (let i = 0, len = str.length; i < len; i++) {
      const code = str.charCodeAt(i);
      if (code < 0x0080) byteLength += 1;
      else if (code < 0x0800) byteLength += 2;
      else if (code < 0xD800) byteLength += 3;
      else if (code < 0xDC00) {
        const next = str.charCodeAt(++i);
        if (i < len && next >= 0xDC00 && next <= 0xDFFF) byteLength += 4;
        else throw new Error('UCS-2 String malformed');
      }
      else if (code < 0xE000) throw new Error('UCS-2 String malformed');
      else byteLength += 3;
    }
    return byteLength;
  }

  return (
    <TopLevelWrapper>
      <InputView>
        <Input
          ref={divRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onBlur={saveSelection}
          data-placeholder={placeholder}
        />
      </InputView>
      <TotalByteWrapper>
        <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.primary} hover='default'>{byte < 90 ? 'SMS' : 'LMS'}</Text>
        <ByteWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>{value === '' ? 0 : byte}</Text>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>/</Text>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='default'>1,500 Byte</Text>
        </ByteWrapper>
      </TotalByteWrapper>
    </TopLevelWrapper>
  );
});

export default EditSettingMessageInput;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 5px;

  width: 100%;
`;

const InputView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 15px;

  box-sizing: border-box;
  width: 100%;
  min-height: 72px;
  max-height: 262px;

  overflow-y: auto;

  border: 1px solid ${props => props.theme.line};
  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  &::-webkit-scrollbar {
    width: 9px;
  }

  &::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.scrollBar};
    border: 3px solid transparent;
    height: 25px;
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px ${props => props.theme.subBackground};
    border: 3px solid transparent;
  }
`;

const Input = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-height: 42px;

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 1.6;

  color: ${props => props.theme.text};

  outline: none;
  word-break: break-word;
  white-space: pre-wrap;

  &:empty::before {
    content: attr(data-placeholder);
    color: ${props => props.theme.gray};
    pointer-events: none;
    white-space: pre-wrap;
  }
`;

const TotalByteWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  margin-top: 10px;

  width: 100%;

  gap: 4px;
`;

const ByteWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: flex-start;

  gap: 4px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.hover};
    }
  }
`;
